import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CreativeMetaOperation } from 'components/common/types/Creative.types';
import { FrameTag } from 'components/common/types/FrameTag.types';
import {
  getActiveFrameTags,
  getCreativeMarketId,
  getIsInsideFrameTags,
} from 'components/pages/ContentManagement/EditCreatives/EditPanel/utils';
import { TaggingRulesModal } from 'components/pages/ContentManagement/Preview/PreviewContent/sections/TaggingRules/TaggingRulesModal/TaggingRulesModal';
import AsyncSelect from 'lib/AsyncSelect';
import { AsyncSelectLoadDataHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import Button, { Color, Size } from 'lib/Button';
import MultipleSwitchBox from 'lib/MultipleSwitchBox';

import { Store } from 'components/common/types/Store.types';
import { updateCreativeProperties } from 'store/creative/reducer';
import { getCreativeFrameTags } from 'store/creative/selectors';
import { getAllSelectedCreativesPendingStatus } from 'store/pages/contentManagement/creatives/selectors';
import {
  updateFrameTagsMode,
  updateIsUnsavedFrameTags,
  updateNewFrameTags,
} from 'store/pages/contentManagement/creativesAdditionalData/reducer';

import { cancelFunctions } from 'utils/cancelFunctions';
import { getFrameTags } from 'modules/api/content';

import crossSvg from 'assets/icons/close.svg';
import plusSvg from 'assets/icons/plus.svg';
import FrameTagCard from './FrameTagCard';

const FrameTags: React.FC = () => {
  const dispatch = useDispatch();
  const areAllItemsPending = useSelector(getAllSelectedCreativesPendingStatus);
  const newFrameTags = useSelector(
    (state: Store) => state.pages.contentManagement.creativesAdditionalData.newFrameTags,
  );
  const frameTagsMode = useSelector(
    (state: Store) => state.pages.contentManagement.creativesAdditionalData.frameTagsMode,
  );
  const selectedCreatives = useSelector((state: Store) => state.pages.contentManagement.creatives.selectedCreatives);
  const marketId = getCreativeMarketId(selectedCreatives[0]);
  const frameTags = useSelector(getCreativeFrameTags);
  const [isFrameTaggingModalOpen, setIsFrameTaggingModalOpen] = useState(false);

  const onLoadFrameTagsLookupData: AsyncSelectLoadDataHandler<FrameTag> = async (_, query) => {
    if (query.length < 2) return [];

    try {
      return await getFrameTags(cancelFunctions, query, marketId);
    } catch (error) {
      return [];
    }
  };

  const onSelectFrameTag = async (_dropdownName: string, newValue: FrameTag): Promise<void> => {
    if (!newValue) return;

    const isInsideFrameTags = getIsInsideFrameTags(newFrameTags, newValue.id);
    if (!isInsideFrameTags) dispatch(updateNewFrameTags([...newFrameTags, { ...newValue, type: 'inclusion' }]));
  };

  const onModeChange = (mode: CreativeMetaOperation): void => {
    dispatch(updateFrameTagsMode(mode));

    const isUnsavedData = getActiveFrameTags(newFrameTags).length > 0;
    const isAddOperation = mode === CreativeMetaOperation.ADD;
    const isUnsavedOperationChange = !isAddOperation || (isAddOperation && isUnsavedData);
    dispatch(updateIsUnsavedFrameTags(isUnsavedOperationChange));
  };

  const onViewDetailsClick = (): void => {
    dispatch(
      updateCreativeProperties({
        frameTags: newFrameTags,
      }),
    );
    setIsFrameTaggingModalOpen(true);
  };

  return (
    <>
      <div data-test-id="frame-tags" className="flex flex-col space-y-2">
        <AsyncSelect
          dataTestId="frame-tags-input"
          name="tags"
          loadData={onLoadFrameTagsLookupData}
          selectedValue={null}
          placeholder="Select a frame tag"
          onSelect={onSelectFrameTag}
          textKey="compositeName"
          isDisabled={!areAllItemsPending}
        />
        <div className="w-min flex justify-start">
          <MultipleSwitchBox
            source={[
              { code: CreativeMetaOperation.ADD, name: 'Add', icon: plusSvg },
              { code: CreativeMetaOperation.REPLACE, name: 'Replace', icon: crossSvg },
            ]}
            selectedValue={frameTagsMode}
            textKey="name"
            valueKey="code"
            onChange={onModeChange}
            isDisabled={!areAllItemsPending}
            dataTestId="frame-tags-switch"
            hideBorder
          />
          <div>
            {newFrameTags.map((tag) => (
              <FrameTagCard
                dataTestId={`frame-tags-card-${tag.id}`}
                key={tag.id}
                frameTag={tag}
                isDisabled={!areAllItemsPending}
              />
            ))}
          </div>
        </div>

        <Button
          size={Size.WIDE}
          color={Color.BLACK_AND_WHITE}
          onClick={onViewDetailsClick}
          svg={plusSvg}
          classNames="h-10"
          label="View details"
        />
      </div>

      <TaggingRulesModal
        isOpen={isFrameTaggingModalOpen}
        frameTags={frameTags}
        marketId={marketId}
        onClose={() => setIsFrameTaggingModalOpen(false)}
      />
    </>
  );
};

export default FrameTags;
